<script setup>
import { ref, computed, watch, onMounted } from "vue";
import {
  getCatalogoProductosCompleto,
  crearProducto,
  actualizarProducto,
  eliminarProducto,
  guardarInventarioInicialBorrador,
  registrarAjusteInventario,
  getConfiguracionInventario,
  activarMotorInventario,
  exportarProductos,
  getPigmentos,
} from "@/utils/api_backend";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import { usePaginacionProductosStore } from "@/store/paginacionStore";
import { useAuthStore } from "@/store/useAuthStore";
import CrudToolbar from "@/components/Shared/CrudToolbar.vue";

const paginacionStore = usePaginacionProductosStore();
const authStore = useAuthStore();
const nombreUsuarioActual = computed(() =>
  `${authStore.userName} ${authStore.userApellido}`.trim() || "—"
);

const TIPOS = [
  { value: "FABRICACION", label: "Fabricación" },
  { value: "IMPORTACION", label: "Importación" },
];

function etiquetaTipo(valor) {
  return TIPOS.find((t) => t.value === valor)?.label ?? "";
}

const registros = ref([]);
const pigmentos = ref([]);
const filtro = ref("");
const loading = ref(false);
const guardando = ref(false);
const exportando = ref(false);
const paginaActual = ref(1);
const modalElement = ref(null);
let modalInstance = null;

const configuracion = ref({
  fecha_corte: null,
  motor_activado: false,
  activado_en: null,
});

const form = ref({
  codigo: "",
  nombre: "",
  modelo: "",
  color: null,
  aditivo: false,
  stock_sistema: 0,
  stock_minimo: 0,
  stock_maximo: 0,
  tipo: "",
});
// Borrador de inventario inicial: input libre inline en la columna
// "Inv. Inicial" de la tabla, editable sin límite mientras el motor de
// inventarios no esté activado (no hay modal ni paso de confirmación).
const guardandoInicialCodigo = ref(null);

// ── Ordenamiento ───────────────────────────────────────────
const sortKey = ref("codigo");
const sortDir = ref("asc");

function onSort({ field, asc }) {
  sortKey.value = field;
  sortDir.value = asc ? "asc" : "desc";
  paginaActual.value = 1;
}

function ordenarPor(field) {
  onSort({
    field,
    asc: sortKey.value === field ? sortDir.value !== "asc" : true,
  });
}

watch(filtro, () => {
  paginaActual.value = 1;
});

// ── Filtros consolidados (icono de embudo en el toolbar) ─────
// En vez de un ColumnFilterMenu por columna (satura el ancho de la tabla),
// un solo panel agrupa el filtro de todas las columnas no-texto.
const FILTRO_CAMPOS = [
  { field: "modelo", label: "Modelo" },
  { field: "color_nombre", label: "Color" },
  { field: "aditivo", label: "Aditivo" },
  { field: "tipo", label: "Tipo" },
  { field: "stock_maximo", label: "Max" },
  { field: "stock_minimo", label: "Min" },
  { field: "activo", label: "Activo" },
];

function valorFiltro(field, item) {
  if (field === "aditivo") return item.aditivo ? "Con aditivo" : "Sin aditivo";
  if (field === "activo") return item.activo ? "Sí" : "No";
  if (field === "tipo") return item.tipo ? etiquetaTipo(item.tipo) : "Sin especificar";
  const v = item[field];
  return v === null || v === undefined || v === "" ? "—" : String(v);
}

function opcionesFiltro(field) {
  const counts = new Map();
  for (const item of registros.value) {
    const v = valorFiltro(field, item);
    counts.set(v, (counts.get(v) || 0) + 1);
  }
  return [...counts.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => a.value.localeCompare(b.value, "es", { numeric: true }));
}

const filtrosColumnas = ref(
  Object.fromEntries(FILTRO_CAMPOS.map((c) => [c.field, new Set()]))
);
const filtroPanelAbierto = ref(false);

const totalFiltrosActivos = computed(
  () => Object.values(filtrosColumnas.value).filter((s) => s.size > 0).length
);

function toggleFiltroValor(field, value) {
  const set = new Set(filtrosColumnas.value[field]);
  if (set.has(value)) set.delete(value);
  else set.add(value);
  filtrosColumnas.value = { ...filtrosColumnas.value, [field]: set };
  paginaActual.value = 1;
}

function limpiarFiltros() {
  filtrosColumnas.value = Object.fromEntries(
    FILTRO_CAMPOS.map((c) => [c.field, new Set()])
  );
  paginaActual.value = 1;
}

function cerrarFiltroPanel() {
  filtroPanelAbierto.value = false;
}

// ── Computed ───────────────────────────────────────────────
const filtrados = computed(() => {
  const s = filtro.value.toLowerCase();
  return registros.value.filter((r) => {
    const coincideBusqueda =
      r.codigo.toLowerCase().includes(s) ||
      r.nombre.toLowerCase().includes(s) ||
      (r.modelo || "").toLowerCase().includes(s);
    if (!coincideBusqueda) return false;

    for (const { field } of FILTRO_CAMPOS) {
      const set = filtrosColumnas.value[field];
      if (set.size > 0 && !set.has(valorFiltro(field, r))) return false;
    }
    return true;
  });
});

const ordenados = computed(() =>
  [...filtrados.value].sort((a, b) => {
    const cmp = String(a[sortKey.value] ?? "").localeCompare(
      String(b[sortKey.value] ?? ""),
      "es",
      { sensitivity: "base", numeric: true }
    );
    return sortDir.value === "asc" ? cmp : -cmp;
  })
);

const porPagina = computed(() => paginacionStore.porPagina);
const totalPaginas = computed(() =>
  Math.max(1, Math.ceil(ordenados.value.length / porPagina.value))
);
const paginados = computed(() => {
  const inicio = (paginaActual.value - 1) * porPagina.value;
  return ordenados.value.slice(inicio, inicio + porPagina.value);
});
const rangoInicio = computed(() =>
  ordenados.value.length === 0 ? 0 : (paginaActual.value - 1) * porPagina.value + 1
);
const rangoFin = computed(() =>
  Math.min(paginaActual.value * porPagina.value, ordenados.value.length)
);
const paginasVisibles = computed(() => {
  const total = totalPaginas.value;
  const actual = paginaActual.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, actual]);
  if (actual > 1) set.add(actual - 1);
  if (actual < total) set.add(actual + 1);
  const ordenadas = [...set].sort((a, b) => a - b);
  const resultado = [];
  for (let i = 0; i < ordenadas.length; i++) {
    if (i > 0 && ordenadas[i] - ordenadas[i - 1] > 1) resultado.push("...");
    resultado.push(ordenadas[i]);
  }
  return resultado;
});

function cambiarPorPagina(n) {
  paginacionStore.setPorPagina(n);
  paginaActual.value = 1;
}

// ── Carga ────────────────────────────────────────────────────
async function cargar() {
  loading.value = true;
  try {
    registros.value = await getCatalogoProductosCompleto();
  } catch {
    Swal.fire("Error", "No se pudieron cargar los productos", "error");
  } finally {
    loading.value = false;
  }
}

async function cargarPigmentos() {
  try {
    pigmentos.value = await getPigmentos();
  } catch {
    pigmentos.value = [];
  }
}

async function cargarConfiguracion() {
  try {
    configuracion.value = await getConfiguracionInventario();
  } catch {
    // Si falla, se asume la postura más conservadora (motor no activado)
    configuracion.value = { fecha_corte: null, motor_activado: false, activado_en: null };
  }
}

// ── Edición ──────────────────────────────────────────────────
function abrirModal(item) {
  form.value = {
    codigo: item.codigo,
    nombre: item.nombre,
    modelo: item.modelo || "",
    color: item.color ?? null,
    aditivo: Boolean(item.aditivo),
    stock_sistema: item.stock_sistema ?? 0,
    stock_minimo: item.stock_minimo ?? 0,
    stock_maximo: item.stock_maximo ?? 0,
    tipo: item.tipo === "IMPORTACION" ? "IMPORTACION" : "FABRICACION",
  };
  colorAbierto.value = false;
  modeloAbierto.value = false;
  modalInstance.show();
}

// ── Inventario inicial inline (columna de la tabla) ───────────
// Input libre: se guarda solo al salir del campo (blur/Enter), no en cada
// tecla. Sigue editable las veces que haga falta hasta activar el motor.
async function guardarInicialFila(item) {
  const valor = Number(item.inventario_inicial) || 0;
  guardandoInicialCodigo.value = item.codigo;
  try {
    await guardarInventarioInicialBorrador(item.codigo, valor);
  } catch (e) {
    const msg = e?.response?.data?.detail;
    Swal.fire(
      "Error",
      Array.isArray(msg)
        ? msg.join(" ")
        : msg || "No se pudo guardar el inventario inicial",
      "error"
    );
    await cargar();
  } finally {
    guardandoInicialCodigo.value = null;
  }
}

function toggleAditivo() {
  form.value.aditivo = !form.value.aditivo;
}

function toggleTipo() {
  form.value.tipo = form.value.tipo === "FABRICACION" ? "IMPORTACION" : "FABRICACION";
}

// ── Dropdown color (mismo patrón que "Área" en CrudOperadores.vue) ──
const colorAbierto = ref(false);

const colorSeleccionado = computed(() =>
  pigmentos.value.find((p) => p.id === form.value.color)
);

function elegirColor(id) {
  form.value.color = id;
  colorAbierto.value = false;
}
function cerrarColor() {
  colorAbierto.value = false;
}

// ── Dropdown Modelo (edición) ─────────────────────────────────
const modeloAbierto = ref(false);
function elegirModelo(modelo) {
  form.value.modelo = modelo;
  modeloAbierto.value = false;
}
function cerrarModelo() {
  modeloAbierto.value = false;
}

const vClickOutside = {
  mounted(el, b) {
    el._co = (e) => {
      if (!el.contains(e.target)) b.value(e);
    };
    document.addEventListener("click", el._co);
  },
  unmounted(el) {
    document.removeEventListener("click", el._co);
  },
};

async function guardar() {
  guardando.value = true;
  try {
    const payload = {
      nombre: form.value.nombre,
      modelo: form.value.modelo,
      color: form.value.color || null,
      aditivo: form.value.aditivo,
      stock_minimo: Number(form.value.stock_minimo) || 0,
      stock_maximo: Number(form.value.stock_maximo) || 0,
      tipo: form.value.tipo,
    };
    await actualizarProducto(form.value.codigo, payload);
    await cargar();
    modalInstance.hide();
    Swal.fire({
      icon: "success",
      title: "Operación exitosa",
      text: "Producto actualizado correctamente",
      timer: 2000,
      showConfirmButton: false,
    });
  } catch {
    Swal.fire("Error", "No se pudo guardar el producto", "error");
  } finally {
    guardando.value = false;
  }
}

async function confirmarBorrado(item) {
  const res = await Swal.fire({
    title: `¿Desactivar "${item.nombre}"?`,
    text: "El producto será marcado como inactivo.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Sí, desactivar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "var(--d-brown)",
    cancelButtonColor: "var(--d-green)",
  });
  if (res.isConfirmed) {
    try {
      await eliminarProducto(item.codigo);
      await cargar();
      Swal.fire({
        icon: "success",
        title: "Listo",
        text: "Producto desactivado",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire("Error", "No se pudo desactivar el producto", "error");
    }
  }
}

// ── Alta de producto ("+ Nuevo producto") ───────────────────
const crearModalElement = ref(null);
let crearModalInstance = null;
const guardandoCrear = ref(false);

const formCrear = ref({
  codigo: "",
  nombre: "",
  modelo: "",
  color: null,
  aditivo: false,
  stock_minimo: 0,
  stock_maximo: 0,
  inventario_inicial: 0,
  tipo: "FABRICACION",
});

function abrirModalCrear() {
  formCrear.value = {
    codigo: "",
    nombre: "",
    modelo: "",
    color: null,
    aditivo: false,
    stock_minimo: 0,
    stock_maximo: 0,
    inventario_inicial: 0,
    tipo: "FABRICACION",
  };
  colorAbiertoCrear.value = false;
  modeloAbiertoCrear.value = false;
  crearModalInstance.show();
}

function toggleAditivoCrear() {
  formCrear.value.aditivo = !formCrear.value.aditivo;
}
function toggleTipoCrear() {
  formCrear.value.tipo =
    formCrear.value.tipo === "FABRICACION" ? "IMPORTACION" : "FABRICACION";
}

const colorAbiertoCrear = ref(false);
const colorSeleccionadoCrear = computed(() =>
  pigmentos.value.find((p) => p.id === formCrear.value.color)
);
function elegirColorCrear(id) {
  formCrear.value.color = id;
  colorAbiertoCrear.value = false;
}
function cerrarColorCrear() {
  colorAbiertoCrear.value = false;
}

// ── Dropdown Modelo (catálogo derivado de los productos existentes) ──
const modeloAbiertoCrear = ref(false);
const modelosDisponibles = computed(() => {
  const unicos = new Set(
    registros.value.map((r) => (r.modelo || "").trim()).filter(Boolean)
  );
  return [...unicos].sort((a, b) => a.localeCompare(b, "es"));
});
function elegirModeloCrear(modelo) {
  formCrear.value.modelo = modelo;
  modeloAbiertoCrear.value = false;
}
function cerrarModeloCrear() {
  modeloAbiertoCrear.value = false;
}

async function guardarCrear() {
  guardandoCrear.value = true;
  try {
    const payload = {
      codigo: formCrear.value.codigo.trim(),
      nombre: formCrear.value.nombre,
      modelo: formCrear.value.modelo,
      color: formCrear.value.color || null,
      aditivo: formCrear.value.aditivo,
      stock_minimo: Number(formCrear.value.stock_minimo) || 0,
      stock_maximo: Number(formCrear.value.stock_maximo) || 0,
      tipo: formCrear.value.tipo,
    };
    if (!configuracion.value.motor_activado) {
      payload.inventario_inicial = Number(formCrear.value.inventario_inicial) || 0;
    }
    await crearProducto(payload);
    await cargar();
    crearModalInstance.hide();
    Swal.fire({
      icon: "success",
      title: "Producto creado",
      text: "El producto se agregó al catálogo correctamente",
      timer: 2200,
      showConfirmButton: false,
    });
  } catch (e) {
    const detalle = e?.response?.data;
    const msg =
      detalle?.codigo?.[0] ||
      (Array.isArray(detalle?.detail) ? detalle.detail.join(" ") : detalle?.detail) ||
      "No se pudo crear el producto";
    Swal.fire("Error", msg, "error");
  } finally {
    guardandoCrear.value = false;
  }
}

// ── Ajuste de inventario ─────────────────────────────────────
const ajusteModalElement = ref(null);
let ajusteModalInstance = null;
const guardandoAjuste = ref(false);
function ahoraDatetimeLocal() {
  return new Date().toISOString().slice(0, 16);
}

const ajusteForm = ref({
  codigo: "",
  cantidad: 0,
  positivo: true,
  motivo: "",
  observaciones: "",
  fecha: ahoraDatetimeLocal(),
});
const buscarProductoAjuste = ref("");
const productoAjusteAbierto = ref(false);

const productoAjusteSeleccionado = computed(() =>
  registros.value.find((r) => r.codigo === ajusteForm.value.codigo)
);
const ajusteInventarioDespues = computed(() => {
  const fisico = productoAjusteSeleccionado.value?.stock_sistema ?? 0;
  const cantidad = Number(ajusteForm.value.cantidad) || 0;
  return ajusteForm.value.positivo ? fisico + cantidad : fisico - cantidad;
});
const opcionesAjuste = computed(() => {
  const s = buscarProductoAjuste.value.toLowerCase();
  return registros.value
    .filter((r) => r.activo)
    .filter(
      (r) =>
        !s || r.codigo.toLowerCase().includes(s) || r.nombre.toLowerCase().includes(s)
    )
    .slice(0, 30);
});

function abrirModalAjuste() {
  ajusteForm.value = {
    codigo: "",
    cantidad: 0,
    positivo: true,
    motivo: "",
    observaciones: "",
    fecha: ahoraDatetimeLocal(),
  };
  buscarProductoAjuste.value = "";
  productoAjusteAbierto.value = false;
  ajusteModalInstance.show();
}
function elegirProductoAjuste(item) {
  ajusteForm.value.codigo = item.codigo;
  productoAjusteAbierto.value = false;
}
function cerrarProductoAjuste() {
  productoAjusteAbierto.value = false;
}

// ── Confirmación del ajuste (segundo modal: resumen + usuario) ──
const confirmarAjusteModalElement = ref(null);
let confirmarAjusteModalInstance = null;

function validarYAbrirConfirmacionAjuste() {
  if (!ajusteForm.value.codigo) {
    Swal.fire("Falta información", "Selecciona un producto", "warning");
    return;
  }
  if (!productoAjusteSeleccionado.value?.inventario_inicial_confirmado) {
    Swal.fire(
      "No disponible",
      "Este producto todavía no tiene su inventario inicial confirmado.",
      "warning"
    );
    return;
  }
  if (!Number(ajusteForm.value.cantidad) || Number(ajusteForm.value.cantidad) <= 0) {
    Swal.fire("Falta información", "Captura una cantidad válida", "warning");
    return;
  }
  if (!ajusteForm.value.motivo || !ajusteForm.value.motivo.trim()) {
    Swal.fire("Falta información", "Captura el motivo del ajuste", "warning");
    return;
  }
  ajusteModalInstance.hide();
  confirmarAjusteModalInstance.show();
}

function cancelarConfirmacionAjuste() {
  confirmarAjusteModalInstance.hide();
  ajusteModalInstance.show();
}

async function guardarAjuste() {
  guardandoAjuste.value = true;
  try {
    await registrarAjusteInventario(ajusteForm.value.codigo, {
      cantidad: Number(ajusteForm.value.cantidad) || 0,
      positivo: ajusteForm.value.positivo,
      motivo: ajusteForm.value.motivo,
      observaciones: ajusteForm.value.observaciones,
      fecha: ajusteForm.value.fecha,
    });
    await cargar();
    confirmarAjusteModalInstance.hide();
    Swal.fire({
      icon: "success",
      title: "Ajuste registrado",
      text: "El saldo del producto fue actualizado",
      timer: 2200,
      showConfirmButton: false,
    });
  } catch (e) {
    const msg = e?.response?.data?.detail;
    Swal.fire(
      "Error",
      Array.isArray(msg) ? msg.join(" ") : msg || "No se pudo registrar el ajuste",
      "error"
    );
  } finally {
    guardandoAjuste.value = false;
  }
}

// ── Configuración / activación del motor de inventarios ──────
const configModalElement = ref(null);
let configModalInstance = null;
const guardandoConfig = ref(false);
const fechaCorteInput = ref("");

function abrirModalConfig() {
  fechaCorteInput.value = "";
  configModalInstance.show();
}

const productosSinCapturar = computed(
  () =>
    registros.value.filter(
      (r) =>
        r.activo &&
        !r.inventario_inicial_confirmado &&
        !(Number(r.inventario_inicial) > 0)
    ).length
);

async function activarMotor() {
  const aviso = await Swal.fire({
    icon: "warning",
    title: "¿Establecer fecha de corte?",
    html:
      "Acción de un solo sentido: bloquea el inventario inicial de todos los productos.<br><br>" +
      (productosSinCapturar.value > 0
        ? `<strong>${productosSinCapturar.value} producto(s)</strong> siguen en 0 y se quedarán así.`
        : "Todos los productos ya tienen un valor capturado."),
    showCancelButton: true,
    confirmButtonText: "Sí, establecer",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "var(--d-brown)",
    cancelButtonColor: "var(--d-green)",
  });
  if (!aviso.isConfirmed) return;

  guardandoConfig.value = true;
  try {
    await activarMotorInventario(fechaCorteInput.value || undefined);
    await cargarConfiguracion();
    await cargar();
    configModalInstance.hide();
    Swal.fire({
      icon: "success",
      title: "Motor de inventarios activado",
      text: "A partir de ahora, todo producto nuevo nace con inventario inicial en 0.",
      timer: 3000,
      showConfirmButton: false,
    });
  } catch (e) {
    const msg = e?.response?.data?.detail;
    Swal.fire(
      "Error",
      Array.isArray(msg) ? msg.join(" ") : msg || "No se pudo activar el motor",
      "error"
    );
  } finally {
    guardandoConfig.value = false;
  }
}

// ── Exportar ───────────────────────────────────────────────
async function exportar() {
  exportando.value = true;
  try {
    await exportarProductos(filtro.value);
  } catch {
    Swal.fire("Error", "No se pudo exportar el catálogo", "error");
  } finally {
    exportando.value = false;
  }
}

onMounted(() => {
  cargar();
  cargarPigmentos();
  cargarConfiguracion();
  modalInstance = new Modal(modalElement.value);
  crearModalInstance = new Modal(crearModalElement.value);
  ajusteModalInstance = new Modal(ajusteModalElement.value);
  confirmarAjusteModalInstance = new Modal(confirmarAjusteModalElement.value);
  configModalInstance = new Modal(configModalElement.value);
});
</script>

<template>
  <div class="crud-container" data-aos="fade-right">
    <CrudToolbar
      title="Productos"
      v-model="filtro"
      searchPlaceholder="Buscar por código, nombre o modelo..."
    >
      <template #actions>
        <div class="filtro-wrapper" v-click-outside="cerrarFiltroPanel">
          <button
            class="btn-header btn-accion btn-filtro"
            type="button"
            @click="filtroPanelAbierto = !filtroPanelAbierto"
            title="Filtros"
          >
            <i class="bi" :class="totalFiltrosActivos > 0 ? 'bi-funnel-fill' : 'bi-funnel'"></i>
            <span class="btn-header-texto ms-2">Filtros</span>
            <span v-if="totalFiltrosActivos > 0" class="filtro-badge">{{ totalFiltrosActivos }}</span>
          </button>

          <div
            v-if="filtroPanelAbierto"
            class="filtro-backdrop"
            @click="cerrarFiltroPanel"
          ></div>
          <div v-if="filtroPanelAbierto" class="filtro-panel">
            <div class="filtro-panel-header">
              <span>Filtros</span>
              <button
                type="button"
                class="filtro-limpiar"
                :disabled="totalFiltrosActivos === 0"
                @click="limpiarFiltros"
              >
                <i class="bi bi-x-circle"></i> Limpiar
              </button>
            </div>
            <div class="filtro-panel-body">
              <div v-for="campo in FILTRO_CAMPOS" :key="campo.field" class="filtro-grupo">
                <p class="filtro-grupo-titulo">{{ campo.label }}</p>
                <label
                  v-for="op in opcionesFiltro(campo.field)"
                  :key="op.value"
                  class="filtro-item"
                >
                  <input
                    type="checkbox"
                    :checked="filtrosColumnas[campo.field].has(op.value)"
                    @change="toggleFiltroValor(campo.field, op.value)"
                  />
                  <span class="filtro-item-label">{{ op.value }}</span>
                  <span class="filtro-item-count">{{ op.count }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>
        <button
          v-if="!configuracion.motor_activado"
          class="btn-header btn-accion btn-config"
          type="button"
          @click="abrirModalConfig"
          title="Configurar fecha de corte y activar el motor de inventarios"
        >
          <i class="bi bi-gear-fill"></i>
          <span class="btn-header-texto ms-2">Configurar corte</span>
        </button>
        <button
          class="btn-header btn-accion btn-crear"
          type="button"
          @click="abrirModalCrear"
        >
          <i class="bi bi-plus-lg"></i>
          <span class="btn-header-texto ms-2">Nuevo producto</span>
        </button>
        <button
          class="btn-header btn-accion btn-ajuste"
          type="button"
          @click="abrirModalAjuste"
        >
          <i class="bi bi-arrow-left-right"></i>
          <span class="btn-header-texto ms-2">Ajuste</span>
        </button>
        <button
          class="btn-header btn-accion btn-exportar"
          type="button"
          :disabled="exportando"
          @click="exportar"
        >
          <span v-if="exportando" class="spinner-border spinner-border-sm"></span>
          <i v-else class="bi bi-file-earmark-excel-fill"></i>
          <span class="btn-header-texto ms-2">Exportar</span>
        </button>
      </template>
    </CrudToolbar>

    <!-- TABLA ESCRITORIO -->
    <div class="divisan-table-container shadow-lg d-none d-lg-block">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>
      <div v-else class="table-scroll">
        <table class="table table-divisan align-middle mb-0">
          <thead>
            <tr>
              <th class="ps-3 th-ordenable th-compacta" @click="ordenarPor('codigo')">
                Código
                <i
                  v-if="sortKey === 'codigo'"
                  class="bi ms-1"
                  :class="sortDir === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'"
                ></i>
              </th>
              <th class="th-ordenable" @click="ordenarPor('nombre')">
                Producto
                <i
                  class="bi ms-1"
                  :class="
                    sortKey === 'nombre'
                      ? sortDir === 'asc'
                        ? 'bi-arrow-up'
                        : 'bi-arrow-down'
                      : 'bi-arrow-down-up th-ordenable-hint'
                  "
                ></i>
              </th>
              <th>Modelo</th>
              <th>Color</th>
              <th class="text-center">Aditivo</th>
              <th class="text-end">Max</th>
              <th class="text-end">Min</th>
              <th class="text-end">Inv. Inicial</th>
              <th>Tipo</th>
              <th class="text-center">Activo</th>
              <th class="text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in paginados"
              :key="item.codigo"
              :class="{ 'fila-inactiva': !item.activo }"
            >
              <td class="ps-3 th-compacta">
                <code class="hex-code">{{ item.codigo }}</code>
              </td>
              <td class="fw-bold text-cafe td-producto">{{ item.nombre }}</td>
              <td class="text-cafe">
                <span v-if="item.modelo">{{ item.modelo }}</span>
                <span v-else class="text-cafe opacity-40 small">—</span>
              </td>
              <td>
                <span
                  v-if="item.color_nombre"
                  class="d-inline-flex align-items-center gap-2"
                >
                  {{ item.color_nombre }}
                </span>
                <span v-else class="text-cafe opacity-40 small">—</span>
              </td>
              <td class="text-center">
                <span :class="['badge-aditivo', { activo: item.aditivo }]">{{
                  item.aditivo ? "C/A" : "S/A"
                }}</span>
              </td>
              <td class="text-end text-cafe opacity-70">{{ item.stock_maximo }}</td>
              <td class="text-end text-cafe opacity-70">{{ item.stock_minimo }}</td>
              <td class="text-end">
                <span v-if="item.inventario_inicial_confirmado" class="fw-bold">{{
                  item.inventario_inicial
                }}</span>
                <input
                  v-else-if="!configuracion.motor_activado"
                  v-model="item.inventario_inicial"
                  type="number"
                  min="0"
                  class="inv-inicial-input"
                  :class="{ guardando: guardandoInicialCodigo === item.codigo }"
                  placeholder="0"
                  @change="guardarInicialFila(item)"
                />
                <span v-else class="text-cafe opacity-40 small">0</span>
              </td>
              <td>
                <span v-if="item.tipo" class="text-cafe">{{
                  etiquetaTipo(item.tipo)
                }}</span>
                <span v-else class="text-cafe opacity-40 small">—</span>
              </td>
              <td class="text-center">
                <span :class="['badge-activo', { si: item.activo }]">{{
                  item.activo ? "Sí" : "No"
                }}</span>
              </td>
              <td class="text-center">
                <div class="d-flex justify-content-center gap-2">
                  <button class="btn-circular" @click="abrirModal(item)" title="Editar">
                    <i class="bi bi-pencil-square"></i>
                  </button>
                  <button
                    v-if="item.activo"
                    class="btn-circular text-danger"
                    @click="confirmarBorrado(item)"
                    title="Desactivar"
                  >
                    <i class="bi bi-trash3-fill"></i>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="paginados.length === 0 && !loading">
              <td colspan="11" class="text-center py-5 text-cafe opacity-50 fw-bold">
                No se encontraron productos
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginación escritorio -->
      <div class="paginacion-wrapper">
        <div class="pag-izq">
          <small class="pag-info"
            >{{ rangoInicio }}–{{ rangoFin }} de {{ ordenados.length }} productos</small
          >
          <div class="por-pagina-wrap">
            <label class="por-pagina-label">Filas:</label>
            <select
              class="por-pagina-select"
              :value="porPagina"
              @change="cambiarPorPagina(Number($event.target.value))"
            >
              <option v-for="n in paginacionStore.opcionesPorPagina" :key="n" :value="n">
                {{ n }}
              </option>
            </select>
          </div>
        </div>
        <div class="pag-der">
          <button
            class="btn-pag"
            :disabled="paginaActual === 1"
            @click="paginaActual = 1"
            title="Primera"
          >
            <i class="bi bi-chevron-double-left"></i>
          </button>
          <button class="btn-pag" :disabled="paginaActual === 1" @click="paginaActual--">
            <i class="bi bi-chevron-left"></i>
          </button>
          <template v-for="p in paginasVisibles" :key="p">
            <span v-if="p === '...'" class="pag-ellipsis">…</span>
            <button
              v-else
              :class="['btn-pag-num', { activo: p === paginaActual }]"
              @click="paginaActual = p"
            >
              {{ p }}
            </button>
          </template>
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual++"
          >
            <i class="bi bi-chevron-right"></i>
          </button>
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual = totalPaginas"
            title="Última"
          >
            <i class="bi bi-chevron-double-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- CARDS MÓVIL -->
    <div class="d-lg-none">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>
      <div
        v-else
        v-for="item in paginados"
        :key="'mob-' + item.codigo"
        class="divisan-mobile-card mb-3 p-3 shadow-sm"
        :class="{ 'fila-inactiva': !item.activo }"
      >
        <div class="d-flex align-items-start justify-content-between gap-2 mb-2">
          <div>
            <code class="hex-code">{{ item.codigo }}</code>
            <div class="fw-bold text-cafe card-name mt-1">{{ item.nombre }}</div>
          </div>
          <div class="d-flex flex-column align-items-end gap-1">
            <span :class="['badge-aditivo', { activo: item.aditivo }]">{{
              item.aditivo ? "C/A" : "S/A"
            }}</span>
            <span :class="['badge-activo', { si: item.activo }]">{{
              item.activo ? "Activo" : "Inactivo"
            }}</span>
          </div>
        </div>
        <div class="card-detalle mb-2">
          <span v-if="item.modelo">{{ item.modelo }}</span>
          <span v-if="item.color_nombre"> · {{ item.color_nombre }}</span>
          <span v-if="item.tipo"> · {{ etiquetaTipo(item.tipo) }}</span>
        </div>
        <div class="card-stock mb-2">
          <span class="fw-bold">{{ item.inventario_inicial }}</span>
          <small class="text-cafe opacity-60">
            inv. inicial · min {{ item.stock_minimo }} / max
            {{ item.stock_maximo }}</small
          >
        </div>
        <div class="d-flex justify-content-end gap-3 pt-2 border-top">
          <i
            class="bi bi-pencil-square text-verde fs-5"
            style="cursor: pointer"
            @click="abrirModal(item)"
          ></i>
          <i
            v-if="item.activo"
            class="bi bi-trash3-fill text-danger fs-5"
            style="cursor: pointer"
            @click="confirmarBorrado(item)"
          ></i>
        </div>
      </div>
      <div class="paginacion-mobile">
        <div class="pag-mobile-top">
          <small class="pag-info"
            >{{ rangoInicio }}–{{ rangoFin }} de {{ ordenados.length }} productos</small
          >
          <div class="por-pagina-wrap">
            <label class="por-pagina-label">Filas:</label>
            <select
              class="por-pagina-select"
              :value="porPagina"
              @change="cambiarPorPagina(Number($event.target.value))"
            >
              <option v-for="n in paginacionStore.opcionesPorPagina" :key="n" :value="n">
                {{ n }}
              </option>
            </select>
          </div>
        </div>
        <div class="pag-mobile-nav">
          <button
            class="btn-pag-mobile"
            :disabled="paginaActual === 1"
            @click="paginaActual--"
          >
            <i class="bi bi-chevron-left"></i> Anterior
          </button>
          <small class="text-verde fw-bold"
            >{{ paginaActual }} / {{ totalPaginas }}</small
          >
          <button
            class="btn-pag-mobile"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual++"
          >
            Siguiente <i class="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL EDICIÓN -->
    <div class="modal fade" tabindex="-1" aria-hidden="true" ref="modalElement">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title fw-bold text-cafe">Editar producto</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <form @submit.prevent="guardar">
            <div class="modal-body">
              <div class="row g-3">
                <div class="col-12 col-md-6">
                  <label class="form-label label-divisan">Código</label>
                  <input
                    :value="form.codigo"
                    type="text"
                    class="form-control divisan-input-modal"
                    disabled
                  />
                </div>
                <div class="col-12 col-md-6">
                  <label class="form-label label-divisan"
                    >Nombre <span class="text-danger">*</span></label
                  >
                  <input
                    v-model="form.nombre"
                    type="text"
                    class="form-control divisan-input-modal"
                    required
                  />
                </div>
              </div>

              <div class="row g-3 mt-1">
                <div class="col-12 col-md-6" v-click-outside="cerrarModelo">
                  <label class="form-label label-divisan">Modelo</label>
                  <div class="custom-select-wrapper">
                    <div
                      class="form-control divisan-input-modal custom-select-input color-display"
                      @click="modeloAbierto = !modeloAbierto"
                    >
                      <span v-if="form.modelo">{{ form.modelo }}</span>
                      <span v-else class="text-muted">Selecciona un modelo</span>
                    </div>
                    <i
                      class="bi custom-select-arrow"
                      :class="modeloAbierto ? 'bi-chevron-up' : 'bi-chevron-down'"
                      @mousedown.prevent="modeloAbierto = !modeloAbierto"
                      style="pointer-events: auto; cursor: pointer"
                    ></i>
                    <div v-if="modeloAbierto" class="custom-dropdown">
                      <div
                        v-for="m in modelosDisponibles"
                        :key="m"
                        class="custom-dropdown-item"
                        :class="{ selected: form.modelo === m }"
                        @mousedown.prevent="elegirModelo(m)"
                      >
                        {{ m }}
                      </div>
                      <div v-if="modelosDisponibles.length === 0" class="custom-dropdown-item text-muted">
                        Sin modelos registrados
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-12 col-md-6" v-click-outside="cerrarColor">
                  <label class="form-label label-divisan">Color</label>
                  <div class="custom-select-wrapper">
                    <div
                      class="form-control divisan-input-modal custom-select-input color-display"
                      @click="colorAbierto = !colorAbierto"
                    >
                      <template v-if="colorSeleccionado">
                        <span
                          class="color-swatch"
                          :style="{ backgroundColor: colorSeleccionado.hexadecimal }"
                        ></span>
                        <span>{{ colorSeleccionado.nombre }}</span>
                      </template>
                      <span v-else class="text-muted">Sin color</span>
                    </div>
                    <i
                      class="bi custom-select-arrow"
                      :class="colorAbierto ? 'bi-chevron-up' : 'bi-chevron-down'"
                      @mousedown.prevent="colorAbierto = !colorAbierto"
                      style="pointer-events: auto; cursor: pointer"
                    ></i>
                    <div v-if="colorAbierto" class="custom-dropdown">
                      <div
                        class="custom-dropdown-item"
                        :class="{ selected: form.color === null }"
                        @mousedown.prevent="elegirColor(null)"
                      >
                        Sin color
                      </div>
                      <div
                        v-for="p in pigmentos"
                        :key="p.id"
                        class="custom-dropdown-item"
                        :class="{ selected: form.color === p.id }"
                        @mousedown.prevent="elegirColor(p.id)"
                      >
                        <span
                          class="color-swatch"
                          :style="{ backgroundColor: p.hexadecimal }"
                        ></span>
                        <span>{{ p.nombre }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="row g-3 mt-1">
                <div class="col-6 col-md-4">
                  <label class="form-label label-divisan small">
                    Stock actual <i class="bi bi-lock-fill opacity-50"></i>
                  </label>
                  <input
                    :value="form.stock_sistema"
                    type="number"
                    class="form-control divisan-input-modal"
                    disabled
                    title="Solo cambia mediante Inventario inicial o un Ajuste de inventario"
                  />
                </div>
                <div class="col-6 col-md-4">
                  <label class="form-label label-divisan small">Stock mín.</label>
                  <input
                    v-model="form.stock_minimo"
                    type="number"
                    min="0"
                    class="form-control divisan-input-modal"
                  />
                </div>
                <div class="col-6 col-md-4">
                  <label class="form-label label-divisan small">Stock máx.</label>
                  <input
                    v-model="form.stock_maximo"
                    type="number"
                    min="0"
                    class="form-control divisan-input-modal"
                  />
                </div>
              </div>

              <div class="row g-3 mt-1">
                <div class="col-6">
                  <label class="form-label label-divisan d-block">Aditivo</label>
                  <button
                    type="button"
                    class="mini-toggle"
                    :class="{ 'is-active': form.aditivo }"
                    role="switch"
                    :aria-checked="form.aditivo"
                    @click="toggleAditivo"
                  >
                    <i
                      class="bi"
                      :class="form.aditivo ? 'bi-check-circle-fill' : 'bi-x-circle'"
                    ></i>
                    <span>{{ form.aditivo ? "Con aditivo" : "Sin aditivo" }}</span>
                  </button>
                </div>
                <div class="col-6">
                  <label class="form-label label-divisan d-block">Tipo</label>
                  <button
                    type="button"
                    class="mini-toggle"
                    :class="{ 'is-active': form.tipo === 'FABRICACION' }"
                    role="switch"
                    :aria-checked="form.tipo === 'FABRICACION'"
                    @click="toggleTipo"
                  >
                    <i
                      class="bi"
                      :class="
                        form.tipo === 'FABRICACION' ? 'bi-tools' : 'bi-globe-americas'
                      "
                    ></i>
                    <span>{{
                      form.tipo === "FABRICACION" ? "Fabricación" : "Importación"
                    }}</span>
                  </button>
                </div>
              </div>
            </div>
            <div class="modal-footer border-0 pt-0">
              <button
                type="submit"
                class="btn btn-nuevo w-100 py-2"
                :disabled="guardando"
              >
                <span
                  v-if="guardando"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                Actualizar producto
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- MODAL NUEVO PRODUCTO -->
    <div class="modal fade" tabindex="-1" aria-hidden="true" ref="crearModalElement">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title fw-bold text-cafe">Nuevo producto</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <form @submit.prevent="guardarCrear">
            <div class="modal-body">
              <div class="row g-3">
                <div class="col-12 col-md-6">
                  <label class="form-label label-divisan"
                    >Código <span class="text-danger">*</span></label
                  >
                  <input
                    v-model="formCrear.codigo"
                    type="text"
                    class="form-control divisan-input-modal"
                    placeholder="Ej. E-RO-1"
                    required
                  />
                </div>
                <div class="col-12 col-md-6">
                  <label class="form-label label-divisan"
                    >Nombre <span class="text-danger">*</span></label
                  >
                  <input
                    v-model="formCrear.nombre"
                    type="text"
                    class="form-control divisan-input-modal"
                    required
                  />
                </div>
              </div>

              <div class="row g-3 mt-1">
                <div class="col-12 col-md-6" v-click-outside="cerrarModeloCrear">
                  <label class="form-label label-divisan">Modelo</label>
                  <div class="custom-select-wrapper">
                    <div
                      class="form-control divisan-input-modal custom-select-input color-display"
                      @click="modeloAbiertoCrear = !modeloAbiertoCrear"
                    >
                      <span v-if="formCrear.modelo">{{ formCrear.modelo }}</span>
                      <span v-else class="text-muted">Selecciona un modelo</span>
                    </div>
                    <i
                      class="bi custom-select-arrow"
                      :class="modeloAbiertoCrear ? 'bi-chevron-up' : 'bi-chevron-down'"
                      @mousedown.prevent="modeloAbiertoCrear = !modeloAbiertoCrear"
                      style="pointer-events: auto; cursor: pointer"
                    ></i>
                    <div v-if="modeloAbiertoCrear" class="custom-dropdown">
                      <div
                        v-for="m in modelosDisponibles"
                        :key="m"
                        class="custom-dropdown-item"
                        :class="{ selected: formCrear.modelo === m }"
                        @mousedown.prevent="elegirModeloCrear(m)"
                      >
                        {{ m }}
                      </div>
                      <div v-if="modelosDisponibles.length === 0" class="custom-dropdown-item text-muted">
                        Sin modelos registrados
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-12 col-md-6" v-click-outside="cerrarColorCrear">
                  <label class="form-label label-divisan">Color</label>
                  <div class="custom-select-wrapper">
                    <div
                      class="form-control divisan-input-modal custom-select-input color-display"
                      @click="colorAbiertoCrear = !colorAbiertoCrear"
                    >
                      <template v-if="colorSeleccionadoCrear">
                        <span
                          class="color-swatch"
                          :style="{ backgroundColor: colorSeleccionadoCrear.hexadecimal }"
                        ></span>
                        <span>{{ colorSeleccionadoCrear.nombre }}</span>
                      </template>
                      <span v-else class="text-muted">Sin color</span>
                    </div>
                    <i
                      class="bi custom-select-arrow"
                      :class="colorAbiertoCrear ? 'bi-chevron-up' : 'bi-chevron-down'"
                      @mousedown.prevent="colorAbiertoCrear = !colorAbiertoCrear"
                      style="pointer-events: auto; cursor: pointer"
                    ></i>
                    <div v-if="colorAbiertoCrear" class="custom-dropdown">
                      <div
                        class="custom-dropdown-item"
                        :class="{ selected: formCrear.color === null }"
                        @mousedown.prevent="elegirColorCrear(null)"
                      >
                        Sin color
                      </div>
                      <div
                        v-for="p in pigmentos"
                        :key="p.id"
                        class="custom-dropdown-item"
                        :class="{ selected: formCrear.color === p.id }"
                        @mousedown.prevent="elegirColorCrear(p.id)"
                      >
                        <span
                          class="color-swatch"
                          :style="{ backgroundColor: p.hexadecimal }"
                        ></span>
                        <span>{{ p.nombre }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="row g-3 mt-1">
                <div class="col-6">
                  <label class="form-label label-divisan small">Stock mín.</label>
                  <input
                    v-model="formCrear.stock_minimo"
                    type="number"
                    min="0"
                    class="form-control divisan-input-modal"
                  />
                </div>
                <div class="col-6">
                  <label class="form-label label-divisan small">Stock máx.</label>
                  <input
                    v-model="formCrear.stock_maximo"
                    type="number"
                    min="0"
                    class="form-control divisan-input-modal"
                  />
                </div>
              </div>

              <div v-if="!configuracion.motor_activado" class="carga-inicial-box mt-3">
                <label class="form-label label-divisan small d-block mb-1">
                  <i class="bi bi-box-seam-fill me-1"></i>Inventario inicial (conteo
                  físico, opcional)
                </label>
                <input
                  v-model="formCrear.inventario_inicial"
                  type="number"
                  min="0"
                  class="form-control divisan-input-modal"
                  placeholder="0"
                />
                <small class="text-cafe opacity-70 d-block mt-1"
                  >Se captura una sola vez al crear el producto y quedará
                  bloqueado.</small
                >
              </div>
              <div v-else class="carga-inicial-box mt-3">
                <small class="text-cafe opacity-70"
                  ><i class="bi bi-lock-fill me-1"></i>El motor de inventarios ya está
                  activado: este producto nacerá con inventario inicial en 0.</small
                >
              </div>

              <div class="row g-3 mt-1">
                <div class="col-6">
                  <label class="form-label label-divisan d-block">Aditivo</label>
                  <button
                    type="button"
                    class="mini-toggle"
                    :class="{ 'is-active': formCrear.aditivo }"
                    role="switch"
                    :aria-checked="formCrear.aditivo"
                    @click="toggleAditivoCrear"
                  >
                    <i
                      class="bi"
                      :class="formCrear.aditivo ? 'bi-check-circle-fill' : 'bi-x-circle'"
                    ></i>
                    <span>{{ formCrear.aditivo ? "Con aditivo" : "Sin aditivo" }}</span>
                  </button>
                </div>
                <div class="col-6">
                  <label class="form-label label-divisan d-block">Tipo</label>
                  <button
                    type="button"
                    class="mini-toggle"
                    :class="{ 'is-active': formCrear.tipo === 'FABRICACION' }"
                    role="switch"
                    :aria-checked="formCrear.tipo === 'FABRICACION'"
                    @click="toggleTipoCrear"
                  >
                    <i
                      class="bi"
                      :class="
                        formCrear.tipo === 'FABRICACION'
                          ? 'bi-tools'
                          : 'bi-globe-americas'
                      "
                    ></i>
                    <span>{{
                      formCrear.tipo === "FABRICACION" ? "Fabricación" : "Importación"
                    }}</span>
                  </button>
                </div>
              </div>
            </div>
            <div class="modal-footer border-0 pt-0">
              <button
                type="submit"
                class="btn btn-nuevo w-100 py-2"
                :disabled="guardandoCrear"
              >
                <span
                  v-if="guardandoCrear"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                Guardar producto
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- MODAL AJUSTE DE INVENTARIO -->
    <div class="modal fade" tabindex="-1" aria-hidden="true" ref="ajusteModalElement">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title fw-bold text-cafe">Ajuste de inventario</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <form @submit.prevent="validarYAbrirConfirmacionAjuste">
            <div class="modal-body">
              <div class="row g-3">
                <div class="col-12 col-md-6" v-click-outside="cerrarProductoAjuste">
                  <label class="form-label label-divisan"
                    >Producto <span class="text-danger">*</span></label
                  >

                  <div class="custom-select-wrapper">
                    <div
                      class="form-control divisan-input-modal custom-select-input color-display"
                      @click="productoAjusteAbierto = !productoAjusteAbierto"
                    >
                      <template v-if="productoAjusteSeleccionado">
                        <span
                          >{{ productoAjusteSeleccionado.codigo }} —
                          {{ productoAjusteSeleccionado.nombre }}</span
                        >
                      </template>
                      <span v-else class="text-muted">Selecciona un producto</span>
                    </div>
                    <i
                      class="bi custom-select-arrow"
                      :class="productoAjusteAbierto ? 'bi-chevron-up' : 'bi-chevron-down'"
                      @mousedown.prevent="productoAjusteAbierto = !productoAjusteAbierto"
                      style="pointer-events: auto; cursor: pointer"
                    ></i>
                    <div v-if="productoAjusteAbierto" class="custom-dropdown">
                      <div class="p-2">
                        <input
                          v-model="buscarProductoAjuste"
                          type="text"
                          class="form-control divisan-input-modal"
                          placeholder="Buscar por código o nombre..."
                          autofocus
                          @click.stop
                        />
                      </div>
                      <div
                        v-for="item in opcionesAjuste"
                        :key="item.codigo"
                        class="custom-dropdown-item"
                        :class="{ selected: ajusteForm.codigo === item.codigo }"
                        @mousedown.prevent="elegirProductoAjuste(item)"
                      >
                        <span>{{ item.codigo }} — {{ item.nombre }}</span>
                      </div>
                      <div
                        v-if="opcionesAjuste.length === 0"
                        class="custom-dropdown-item text-muted"
                      >
                        Sin resultados
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-12 col-md-6">
                  <label class="form-label label-divisan"
                    >Fecha del ajuste <span class="text-danger">*</span></label
                  >
                  <input
                    v-model="ajusteForm.fecha"
                    type="datetime-local"
                    class="form-control divisan-input-modal"
                    required
                  />
                </div>
              </div>

              <div class="mb-3">
                <div v-if="productoAjusteSeleccionado" class="producto-info-card mt-2">
                  <div class="producto-info-top">
                    <div class="producto-info-grid producto-info-grid--fila">
                      <div class="producto-info-dato d-none d-md-flex">
                        <span class="producto-info-label">Código</span>
                        <span class="producto-info-valor">{{ productoAjusteSeleccionado.codigo }}</span>
                      </div>
                      <div class="producto-info-dato producto-info-dato--nombre">
                        <span class="producto-info-label">Producto</span>
                        <span class="producto-info-valor">{{ productoAjusteSeleccionado.nombre }}</span>
                      </div>
                      <div class="producto-info-dato d-none d-md-flex">
                        <span class="producto-info-label">Modelo</span>
                        <span class="producto-info-valor">{{ productoAjusteSeleccionado.modelo || "—" }}</span>
                      </div>
                      <div class="producto-info-dato d-none d-md-flex">
                        <span class="producto-info-label">Color</span>
                        <span class="producto-info-valor">{{ productoAjusteSeleccionado.color_nombre || "—" }}</span>
                      </div>
                      <div class="producto-info-dato d-none d-md-flex">
                        <span class="producto-info-label">Aditivo</span>
                        <span class="producto-info-valor">{{ productoAjusteSeleccionado.aditivo ? "C/A" : "S/A" }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="producto-stats-row">
                    <div class="producto-stat">
                      <i class="bi bi-box-seam-fill"></i>
                      <div>
                        <span class="producto-stat-label">Inventario físico actual</span>
                        <span class="producto-stat-valor"
                          >{{ productoAjusteSeleccionado.stock_sistema }} pzas</span
                        >
                      </div>
                    </div>
                    <div class="producto-stat">
                      <i class="bi bi-arrow-left-right"></i>
                      <div>
                        <span class="producto-stat-label">Comprometido actual</span>
                        <span class="producto-stat-valor"
                          >{{ productoAjusteSeleccionado.comprometido ?? 0 }} pzas</span
                        >
                      </div>
                    </div>
                    <div class="producto-stat">
                      <i class="bi bi-clipboard-check-fill"></i>
                      <div>
                        <span class="producto-stat-label">Disponible actual</span>
                        <span class="producto-stat-valor"
                          >{{ productoAjusteSeleccionado.disponible ?? productoAjusteSeleccionado.stock_sistema }} pzas</span
                        >
                      </div>
                    </div>
                  </div>
                </div>

                <small
                  v-if="
                    productoAjusteSeleccionado &&
                    !productoAjusteSeleccionado.inventario_inicial_confirmado
                  "
                  class="text-danger d-block mt-2"
                >
                  <i class="bi bi-exclamation-triangle-fill me-1"></i>Este producto no
                  tiene inventario inicial confirmado todavía.
                </small>
              </div>

              <div class="row g-3">
                <div class="col-6">
                  <label class="form-label label-divisan d-block">Movimiento</label>
                  <button
                    type="button"
                    class="mini-toggle"
                    :class="{ 'is-active': ajusteForm.positivo }"
                    role="switch"
                    :aria-checked="ajusteForm.positivo"
                    @click="ajusteForm.positivo = !ajusteForm.positivo"
                  >
                    <i
                      class="bi"
                      :class="
                        ajusteForm.positivo
                          ? 'bi-plus-circle-fill'
                          : 'bi-dash-circle-fill'
                      "
                    ></i>
                    <span>{{ ajusteForm.positivo ? "Entrada (+)" : "Salida (−)" }}</span>
                  </button>
                </div>
                <div class="col-6">
                  <label class="form-label label-divisan small">Cantidad</label>
                  <input
                    v-model="ajusteForm.cantidad"
                    type="number"
                    min="1"
                    class="form-control divisan-input-modal"
                    required
                  />
                </div>
              </div>

              <div class="row g-3 mt-1">
                <div class="col-12 col-md-6">
                  <label class="form-label label-divisan small"
                    >Motivo <span class="text-danger">*</span></label
                  >
                  <textarea
                    v-model="ajusteForm.motivo"
                    class="form-control divisan-input-modal"
                    rows="2"
                    placeholder="Ej. Conteo físico de cierre de mes"
                    required
                  ></textarea>
                </div>
                <div class="col-12 col-md-6">
                  <label class="form-label label-divisan small">Observaciones</label>
                  <textarea
                    v-model="ajusteForm.observaciones"
                    class="form-control divisan-input-modal"
                    rows="2"
                    placeholder="Información adicional del ajuste (opcional)"
                  ></textarea>
                </div>
              </div>
            </div>
            <div class="modal-footer border-0 pt-0">
              <button type="submit" class="btn btn-nuevo w-100 py-2">
                Continuar <i class="bi bi-arrow-right ms-1"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- MODAL CONFIRMAR AJUSTE DE INVENTARIO -->
    <div class="modal fade" tabindex="-1" aria-hidden="true" ref="confirmarAjusteModalElement">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title fw-bold text-cafe">Confirmar ajuste de inventario</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <p class="text-cafe opacity-80 mb-3" style="font-size: 0.85rem">
              Revisa el resumen antes de aplicar el ajuste a
              <strong>{{ productoAjusteSeleccionado?.nombre }}</strong
              >.
            </p>
            <div class="ajuste-resumen-card">
              <div class="ajuste-resumen-fila">
                <span>Inventario físico actual</span>
                <strong
                  >{{ (productoAjusteSeleccionado?.stock_sistema ?? 0).toLocaleString("es-MX") }}
                  pzas</strong
                >
              </div>
              <div class="ajuste-resumen-fila">
                <span>Ajuste</span>
                <strong
                  :class="ajusteForm.positivo ? 'ajuste-resumen-positivo' : 'ajuste-resumen-negativo'"
                >
                  {{ ajusteForm.positivo ? "+" : "−"
                  }}{{ (Number(ajusteForm.cantidad) || 0).toLocaleString("es-MX") }} pzas
                </strong>
              </div>
              <div class="ajuste-resumen-divisor"></div>
              <div class="ajuste-resumen-fila ajuste-resumen-fila--total">
                <span>Inventario después del ajuste</span>
                <strong>{{ ajusteInventarioDespues.toLocaleString("es-MX") }} pzas</strong>
              </div>
              <div class="ajuste-resumen-divisor"></div>
              <div class="ajuste-resumen-fila">
                <span>Usuario</span>
                <strong>{{ nombreUsuarioActual }}</strong>
              </div>
              <div class="ajuste-resumen-nota">
                <i class="bi bi-info-circle-fill"></i>
                <span
                  >Este ajuste se reflejará inmediatamente en el inventario del producto y
                  se registrará en el historial de movimientos.</span
                >
              </div>
            </div>
          </div>
          <div class="modal-footer border-0 pt-0 d-flex gap-2">
            <button
              type="button"
              class="btn-cancelar-ajuste"
              :disabled="guardandoAjuste"
              @click="cancelarConfirmacionAjuste"
            >
              Regresar
            </button>
            <button
              type="button"
              class="btn btn-nuevo flex-fill py-2"
              :disabled="guardandoAjuste"
              @click="guardarAjuste"
            >
              <span v-if="guardandoAjuste" class="spinner-border spinner-border-sm me-2"></span>
              <i v-else class="bi bi-check-lg me-1"></i>
              Confirmar ajuste
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL CONFIGURACIÓN / ACTIVACIÓN DEL MOTOR -->
    <div class="modal fade" tabindex="-1" aria-hidden="true" ref="configModalElement">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title fw-bold text-cafe">Fecha de corte del inventario</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <p class="text-cafe opacity-80" style="font-size: 0.82rem">
              Bloquea el inventario inicial de todos los productos con el valor que
              tengan en ese momento.
            </p>
            <p v-if="productosSinCapturar > 0" class="aviso-corte mb-3">
              <i class="bi bi-exclamation-triangle-fill me-1"></i>
              {{ productosSinCapturar }} producto(s) siguen en 0.
            </p>
            <label class="form-label label-divisan small">Fecha y hora de corte</label>
            <input
              v-model="fechaCorteInput"
              type="datetime-local"
              class="form-control divisan-input-modal"
            />
            <small class="text-cafe opacity-60 d-block mt-1"
              >Si se deja en blanco, se usa la fecha y hora actual.</small
            >
          </div>
          <div class="modal-footer border-0 pt-0">
            <button
              type="button"
              class="btn btn-nuevo w-100 py-2"
              :disabled="guardandoConfig"
              @click="activarMotor"
            >
              <span
                v-if="guardandoConfig"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Activar motor de inventarios
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}

.btn-nuevo {
  background-color: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
}
.btn-nuevo:hover:not(:disabled) {
  background-color: #703808;
  color: var(--d-cream);
}
.btn-header {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}
@media (max-width: 991.98px) {
  .btn-header-texto {
    display: none;
  }
  .btn-header {
    width: 36px;
    height: 36px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
  }
}
.btn-accion {
  border-radius: var(--d-radius-pill);
  padding: 7px 16px;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s;
}
.btn-accion:disabled {
  opacity: 0.6;
  cursor: default;
}

/* Configurar corte: outline punteado, discreto — acción de setup única */
.btn-config {
  background: transparent;
  border: 1.5px dashed var(--d-green);
  color: var(--d-green);
}
.btn-config:hover {
  background: rgba(96, 108, 56, 0.08);
}

/* Nuevo producto: acción primaria, verde sólido */
.btn-crear {
  background: var(--d-green);
  border: 1.5px solid var(--d-green);
  color: var(--d-cream);
}
.btn-crear:hover {
  background: var(--d-green-dark);
  border-color: var(--d-green-dark);
}

/* Ajuste de inventario: outline café */
.btn-ajuste {
  background: white;
  border: 1.5px solid var(--d-brown);
  color: var(--d-brown);
}
.btn-ajuste:hover {
  background: rgba(64, 34, 8, 0.06);
}

/* Exportar: sólido azul, distinto de las acciones de escritura */
.btn-exportar {
  background: var(--d-info);
  border: 1.5px solid var(--d-info);
  color: white;
}
.btn-exportar:hover:not(:disabled) {
  background: #0d47a1;
  border-color: #0d47a1;
}

/* Filtros: neutro, con badge de conteo cuando hay filtros activos */
.filtro-wrapper {
  position: relative;
}
.btn-filtro {
  background: white;
  border: 1.5px solid rgba(64, 34, 8, 0.3);
  color: var(--d-brown);
  position: relative;
}
.btn-filtro:hover {
  background: rgba(64, 34, 8, 0.06);
}
.filtro-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  margin-left: 6px;
  border-radius: 999px;
  background: var(--d-green);
  color: white;
  font-size: 0.65rem;
  font-weight: 800;
}
.filtro-panel {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 1090;
  width: 280px;
  max-height: 420px;
  display: flex;
  flex-direction: column;
  background: white;
  border: 1.5px solid var(--d-green);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(40, 54, 24, 0.2);
}
.filtro-backdrop {
  display: none;
}
/* En móvil el botón queda dentro de una fila que se envuelve/reordena;
   anclar el panel a la derecha del botón lo saca de pantalla. Se centra
   como un modal flotante en su lugar, con fondo para cerrarlo al tocar fuera. */
@media (max-width: 767.98px) {
  .filtro-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 1089;
    background: rgba(20, 30, 40, 0.45);
  }
  .filtro-panel {
    position: fixed;
    top: 50%;
    left: 50%;
    right: auto;
    transform: translate(-50%, -50%);
    width: min(90vw, 320px);
    max-height: 80vh;
  }
}
.filtro-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid var(--d-border);
  font-weight: 800;
  color: var(--d-brown);
  font-size: 0.85rem;
}
.filtro-limpiar {
  border: none;
  background: transparent;
  color: var(--d-brown);
  font-size: 0.76rem;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.filtro-limpiar:disabled {
  opacity: 0.35;
  cursor: default;
}
.filtro-panel-body {
  overflow-y: auto;
  padding: 6px 4px;
}
.filtro-grupo {
  padding: 6px 10px;
  border-bottom: 1px solid rgba(64, 34, 8, 0.08);
}
.filtro-grupo:last-child {
  border-bottom: none;
}
.filtro-grupo-titulo {
  margin: 0 0 4px;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--d-green);
}
.filtro-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 4px;
  border-radius: 6px;
  font-size: 0.8rem;
  color: var(--d-brown);
  cursor: pointer;
}
.filtro-item:hover {
  background: rgba(96, 108, 56, 0.08);
}
.filtro-item-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.filtro-item-count {
  color: var(--d-text-muted, rgba(64, 34, 8, 0.5));
  font-size: 0.7rem;
}

.badge-activo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 38px;
  padding: 3px 8px;
  border-radius: 6px;
  background: rgba(64, 34, 8, 0.08);
  color: var(--d-brown);
  opacity: 0.6;
  font-size: 0.74rem;
  font-weight: 800;
}
.badge-activo.si {
  background: rgba(46, 125, 50, 0.14);
  color: #2e7d32;
  opacity: 1;
}
.fila-inactiva {
  opacity: 0.55;
}

.carga-inicial-box {
  background: rgba(96, 108, 56, 0.06);
  border: 1px dashed var(--d-green);
  border-radius: 10px;
  padding: 10px 12px;
}

.inv-inicial-input {
  width: 84px;
  height: 30px;
  border: 1.5px dashed var(--d-green);
  border-radius: 6px;
  background: rgba(96, 108, 56, 0.05);
  color: var(--d-brown);
  font-weight: 700;
  font-size: 0.82rem;
  text-align: right;
  padding: 0 8px;
  outline: none;
  transition: border-color 0.15s, background 0.15s;
  /* Sin flechas de incremento/decremento: cada clic dispararía un PATCH */
  -moz-appearance: textfield;
  appearance: textfield;
}
.inv-inicial-input::-webkit-outer-spin-button,
.inv-inicial-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.inv-inicial-input:focus {
  border-style: solid;
  background: white;
}
.inv-inicial-input.guardando {
  border-color: var(--d-info);
  opacity: 0.7;
}

.aviso-corte {
  background: rgba(183, 119, 13, 0.1);
  border: 1px solid var(--d-warning);
  color: var(--d-warning);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 0.8rem;
  font-weight: 700;
}

.divisan-table-container {
  background-color: var(--d-cream);
  border-radius: var(--d-radius-lg);
  box-shadow: var(--d-elevation-2);
  overflow: hidden;
}
.table-scroll {
  overflow-x: auto;
}
.table-divisan thead th {
  background: var(--d-cream);
  color: var(--d-brown);
  border-bottom: 1px solid var(--d-border);
}
.table-divisan th {
  padding: 15px;
  font-weight: 800;
  text-transform: capitalize;
  border: none;
  white-space: nowrap;
}
.table-divisan td {
  border-bottom: 1px solid rgba(96, 108, 56, 0.1);
  padding: 12px 15px;
  white-space: nowrap;
}
.table-divisan tbody tr:hover td {
  background-color: rgba(96, 108, 56, 0.05);
}

.hex-code {
  background: rgba(96, 108, 56, 0.1);
  color: var(--d-green-dark);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.76rem;
}
.th-compacta {
  width: 1%;
  white-space: nowrap;
}
.td-producto {
  font-size: 0.83rem;
}
.th-ordenable {
  cursor: pointer;
  user-select: none;
  transition: color 0.15s;
}
.th-ordenable:hover {
  color: var(--d-green);
}
.th-ordenable i {
  font-size: 0.7rem;
  color: var(--d-green);
}
.th-ordenable-hint {
  opacity: 0.35;
  transition: opacity 0.15s;
}
.th-ordenable:hover .th-ordenable-hint {
  opacity: 0.8;
}

.badge-aditivo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 38px;
  padding: 3px 8px;
  border-radius: 6px;
  background: rgba(64, 34, 8, 0.08);
  color: var(--d-brown);
  opacity: 0.6;
  font-size: 0.74rem;
  font-weight: 800;
}
.badge-aditivo.activo {
  background: rgba(46, 125, 50, 0.14);
  color: #2e7d32;
  opacity: 1;
}

.btn-circular {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--d-green);
  transition: 0.2s;
}
.btn-circular:hover {
  background-color: var(--d-green);
  color: var(--d-cream);
}

.divisan-modal {
  background-color: var(--d-cream);
  border: 2px solid var(--d-brown);
  border-radius: 20px;
}
/* El modal centrado quedaba pegado (a veces debajo) del navbar fijo; se
   empuja un poco hacia abajo en todos los tamaños. */
.modal-dialog {
  margin-top: 4.5rem;
}
.label-divisan {
  color: var(--d-brown);
  font-weight: 700;
}
.divisan-input-modal {
  border: 1px solid var(--d-green);
  border-radius: 8px;
}
.divisan-input-modal:disabled {
  background-color: rgba(96, 108, 56, 0.08);
  opacity: 0.8;
}

/* ── Toggle Aditivo / Tipo ──────────────────────────────────── */
.mini-toggle {
  width: 100%;
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 8px;
  border: 1.5px solid var(--d-green);
  background: white;
  color: var(--d-brown);
  font-weight: 700;
  font-size: 0.88rem;
  cursor: pointer;
  transition: all 0.15s;
}
.mini-toggle:hover {
  background: rgba(96, 108, 56, 0.06);
}
.mini-toggle.is-active {
  background: #2e7d32;
  border-color: #2e7d32;
  color: white;
}

/* ── Custom select (color) ─────────────────────────────────── */
.custom-select-wrapper {
  position: relative;
}
.custom-select-input {
  padding-right: 2.2rem !important;
}
.color-display {
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
}
.custom-select-arrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--d-green);
  font-size: 0.8rem;
  cursor: pointer;
}
.custom-dropdown {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  z-index: 1080;
  background: white;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(40, 54, 24, 0.15);
  max-height: 185px;
  overflow-y: auto;
}
.custom-dropdown-item {
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  gap: 8px;
  align-items: center;
  border-bottom: 1px solid rgba(96, 108, 56, 0.08);
  transition: background 0.12s;
}
.custom-dropdown-item:last-child {
  border-bottom: none;
}
.custom-dropdown-item:hover,
.custom-dropdown-item.selected {
  background: rgba(96, 108, 56, 0.12);
}
.color-swatch {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid rgba(64, 34, 8, 0.2);
  flex-shrink: 0;
}

/* ── Tarjeta de información del producto seleccionado ────────── */
.producto-info-card {
  background: rgba(96, 108, 56, 0.05);
  border: 1.5px solid var(--d-green);
  border-radius: 10px;
  padding: 12px;
}
.producto-info-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}
.producto-info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px 16px;
  flex: 1;
  min-width: 0;
}
/* En desktop hay espacio de sobra (modal ensanchado a modal-lg): todo el
   detalle del producto cabe en un solo renglón. */
.producto-info-grid--fila {
  display: flex;
  gap: 18px;
}
.producto-info-dato {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}
.producto-info-grid--fila .producto-info-dato {
  flex: 1 1 0;
}
.producto-info-grid--fila .producto-info-dato--nombre {
  flex: 2 1 0;
}
.producto-info-dato--nombre {
  grid-column: span 2;
}
.producto-info-label {
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--d-green);
  opacity: 0.8;
}
.producto-info-valor {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--d-brown);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.producto-stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(96, 108, 56, 0.2);
}
.producto-stat {
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  border: 1px solid rgba(96, 108, 56, 0.15);
  border-radius: 8px;
  padding: 8px 10px;
}
.producto-stat i {
  color: var(--d-green);
  font-size: 1.1rem;
  flex-shrink: 0;
}
.producto-stat > div {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.producto-stat-label {
  font-size: 0.62rem;
  font-weight: 700;
  color: var(--d-brown);
  opacity: 0.65;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.producto-stat-valor {
  font-size: 0.9rem;
  font-weight: 800;
  color: var(--d-brown);
}

/* ── Resumen del ajuste ───────────────────────────────────────── */
.ajuste-resumen-card {
  background: rgba(96, 108, 56, 0.05);
  border: 1.5px solid var(--d-green);
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
}
.ajuste-resumen-fila {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 0.82rem;
  color: var(--d-brown);
  padding: 3px 0;
}
.ajuste-resumen-fila strong {
  font-weight: 800;
}
.ajuste-resumen-positivo {
  color: #2e7d32;
}
.ajuste-resumen-negativo {
  color: #c0392b;
}
.ajuste-resumen-divisor {
  height: 1px;
  background: rgba(96, 108, 56, 0.25);
  margin: 4px 0;
}
.ajuste-resumen-fila--total {
  font-size: 0.9rem;
}
.ajuste-resumen-fila--total strong {
  color: var(--d-green-dark);
  font-size: 1rem;
}
.ajuste-resumen-nota {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed rgba(96, 108, 56, 0.25);
  font-size: 0.72rem;
  color: var(--d-green-dark);
  line-height: 1.35;
}
.ajuste-resumen-nota i {
  flex-shrink: 0;
  margin-top: 1px;
}

.btn-cancelar-ajuste {
  background: white;
  border: 1.5px solid var(--d-brown);
  color: var(--d-brown);
  border-radius: 8px;
  padding: 0.5rem 1.2rem;
  font-weight: 700;
  font-size: 0.88rem;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-cancelar-ajuste:hover:not(:disabled) {
  background: rgba(64, 34, 8, 0.06);
}
.btn-cancelar-ajuste:disabled {
  opacity: 0.5;
  cursor: default;
}


@media (max-width: 767.98px) {
  .producto-info-grid--fila {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
  .producto-info-grid--fila .producto-info-dato--nombre {
    grid-column: span 2;
  }
}
@media (max-width: 576px) {
  .producto-stats-row {
    grid-template-columns: 1fr;
  }
}

.divisan-mobile-card {
  background-color: var(--d-cream);
  border-radius: 15px;
  border-left: 6px solid var(--d-green);
}
.card-name {
  font-size: 0.92rem;
}
.card-detalle {
  font-size: 0.8rem;
  color: rgba(64, 34, 8, 0.75);
}
.card-stock {
  font-size: 0.85rem;
  color: var(--d-brown);
}

.paginacion-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  flex-wrap: wrap;
  gap: 8px;
  border-top: 1px solid rgba(64, 34, 8, 0.1);
  background: rgba(96, 108, 56, 0.05);
}
.pag-izq {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.pag-der {
  display: flex;
  align-items: center;
  gap: 4px;
}
.pag-info {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}
.por-pagina-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-select {
  appearance: none;
  -webkit-appearance: none;
  background: white
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23606c38' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E")
    no-repeat right 8px center;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  padding: 3px 26px 3px 10px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--d-brown);
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s;
  min-width: 58px;
}
.por-pagina-select:focus,
.por-pagina-select:hover {
  border-color: var(--d-brown);
}
.btn-pag {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.35);
  color: var(--d-brown);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.8rem;
}
.btn-pag:hover:not(:disabled) {
  background: var(--d-green);
  color: var(--d-cream);
  border-color: var(--d-green);
}
.btn-pag:disabled {
  opacity: 0.3;
  cursor: default;
}
.btn-pag-num {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.25);
  color: var(--d-brown);
  border-radius: 8px;
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.82rem;
  font-weight: 700;
}
.btn-pag-num:hover {
  background: rgba(96, 108, 56, 0.1);
  border-color: var(--d-green);
}
.btn-pag-num.activo {
  background: var(--d-brown);
  color: var(--d-cream);
  border-color: var(--d-brown);
  pointer-events: none;
}
.pag-ellipsis {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 32px;
  font-size: 0.85rem;
  color: var(--d-brown);
  opacity: 0.4;
  font-weight: 700;
}

.paginacion-mobile {
  border-top: 1px solid rgba(64, 34, 8, 0.15);
  padding: 10px 4px;
  margin-top: 4px;
}
.pag-mobile-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.pag-mobile-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.btn-pag-mobile {
  background: var(--d-cream);
  border: 1px solid var(--d-green);
  color: var(--d-brown);
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 110px;
  justify-content: center;
}
.btn-pag-mobile:hover:not(:disabled) {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-pag-mobile:disabled {
  opacity: 0.35;
  cursor: default;
}

@media (max-width: 768px) {
  .paginacion-wrapper {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}
</style>
